import {
  getLocalizedDefaults,
  SUPPORTED_LANGUAGES,
  type CalendarStatusLabels,
  type SupportedLanguage,
} from '../localized-defaults';
import type { ConfirmationStatus } from '../events/confirmation-status';

export type CalendarLabelKind = keyof CalendarStatusLabels;

type StatusPrefixEntry = {
  status: CalendarLabelKind;
  prefix: string;
};

function buildAllStatusPrefixEntries(): StatusPrefixEntry[] {
  const seenPrefixes = new Set<string>();
  const entries: StatusPrefixEntry[] = [];

  for (const language of SUPPORTED_LANGUAGES) {
    const labels = getLocalizedDefaults(language).calendarStatusLabels;
    for (const status of Object.keys(labels) as CalendarLabelKind[]) {
      const prefix = labels[status];
      if (seenPrefixes.has(prefix)) continue;
      seenPrefixes.add(prefix);
      entries.push({ status, prefix });
    }
  }

  return entries.sort((a, b) => b.prefix.length - a.prefix.length);
}

const ALL_STATUS_PREFIX_ENTRIES = buildAllStatusPrefixEntries();

/**
 * `applyStatus` writes `"<Label>: <title>"`. Read it back the SAME way, or a
 * customer's own title ("Cancelled per Valarie") silently becomes an app
 * status and, for `cancelled`, silently turns their reminders off (S-588).
 * The colon is required; the space after it is not, so a hand-typed
 * "Cancelled:X" still counts.
 */
function matchWrittenStatusPrefix(
  title: string,
): { entry: StatusPrefixEntry; separatorLength: number } | null {
  for (const entry of ALL_STATUS_PREFIX_ENTRIES) {
    if (title.startsWith(`${entry.prefix}:`)) {
      return { entry, separatorLength: entry.prefix.length + 1 };
    }
  }
  return null;
}

// T-22: trim between labels, or a spaced stack ("Pending: Confirmed: X") stops at the second.
function walkStackedLabels(title: string): {
  kinds: CalendarLabelKind[];
  rest: string;
} {
  const kinds: CalendarLabelKind[] = [];
  let rest = title;
  let match = matchWrittenStatusPrefix(rest);

  while (match) {
    kinds.push(match.entry.status);
    rest = rest.slice(match.separatorLength).trimStart();
    match = matchWrittenStatusPrefix(rest);
  }

  return { kinds, rest };
}

export class ConfirmationEventTitleHelper {
  static getStatusPrefixes(
    language: SupportedLanguage = 'en',
  ): CalendarStatusLabels {
    return getLocalizedDefaults(language).calendarStatusLabels;
  }

  static getLabelKindFromTitle(
    title: string | null | undefined,
  ): CalendarLabelKind | null {
    if (!title) return null;

    return matchWrittenStatusPrefix(title)?.entry.status ?? null;
  }

  /**
   * The status `events.confirmed` stores. A late cancel is a label only
   * (S-625): it must read as `cancelled`, or every reminder gate misses it.
   */
  static getStatusFromTitle(
    title: string | null | undefined,
  ): ConfirmationStatus | null {
    const kind = this.getLabelKindFromTitle(title);

    return kind === 'lateCancelled' ? 'cancelled' : kind;
  }

  static applyStatus(
    title: string,
    status: string,
    language: SupportedLanguage = 'en',
  ): string {
    if (!title) return title;

    const clearedTitle = this.removeStatus(title);
    const labels = getLocalizedDefaults(language).calendarStatusLabels;
    const statusPrefix = labels[status as CalendarLabelKind];

    if (!statusPrefix) return title;

    return title.length
      ? this.trim(`${statusPrefix}: ${clearedTitle}`)
      : status;
  }

  /** Every label at the start of the title, outermost first. */
  static getStackedLabelKinds(title: string): CalendarLabelKind[] {
    if (!title) return [];

    return walkStackedLabels(title).kinds;
  }

  static removeStatus(title: string): string {
    if (!title) return title;

    return this.trim(walkStackedLabels(title).rest);
  }

  private static trim(title: string): string {
    return title.replace(/^:/, '').trim();
  }
}
